import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor, CapacitorHttp, HttpResponse as CapacitorHttpResponse } from '@capacitor/core';
import { Observable, from } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthUserService } from '../services/auth/auth-user.service';
import { AuthService } from '../services/http/auth/auth.service';
import { SchoolYearRegistry } from '../services/date-manager/school-year/school-year.registry';
import { LocalStorageService } from '../services/local-storage/local-storage.service';

/**
 * Routes API calls through Capacitor's native HTTP stack on Android/iOS so WebView
 * CORS (origin https://localhost) does not block requests — no backend changes required.
 */
@Injectable()
export class CapacitorNativeHttpInterceptor implements HttpInterceptor {
  private readonly apiOriginPrefixes = this.buildApiOriginPrefixes();

  constructor(
    private authUserService: AuthUserService,
    private schoolYearRegistry: SchoolYearRegistry,
    private localStorageService: LocalStorageService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.shouldUseNativeHttp(req)) {
      return next.handle(req);
    }

    return from(this.executeNativeRequest(req));
  }

  private shouldUseNativeHttp(req: HttpRequest<unknown>): boolean {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    if (req.responseType === 'blob' || req.responseType === 'arraybuffer') {
      return false;
    }

    if (req.body instanceof FormData || req.body instanceof Blob) {
      return false;
    }

    return this.apiOriginPrefixes.some(prefix => req.url.startsWith(prefix));
  }

  private buildNativeHeaders(req: HttpRequest<unknown>): Record<string, string> {
    const headers: Record<string, string> = {};

    req.headers.keys().forEach(key => {
      const values = req.headers.getAll(key);
      if (values?.length) {
        const headerName = key.toLowerCase() === 'authorization' ? 'Authorization' : key;
        headers[headerName] = values.join(',');
      }
    });

    if (req.url.includes(AuthService.SIMULATE_ENDPOINT)) {
      const adminToken = this.getAdminToken();
      if (adminToken) {
        headers['Authorization'] = `Bearer ${adminToken}`;
      }

      const adminSchoolUuid = this.authUserService.getSchoolUserAuthority()?.uuid;
      const schoolYear = this.schoolYearRegistry.selectedSchoolYear?.schoolYear;

      if (adminSchoolUuid) {
        headers['X-Requested-School'] = adminSchoolUuid;
      }
      if (schoolYear) {
        headers['X-Requested-SchoolYear'] = schoolYear.toString();
      }
    }

    if (
      req.body != null &&
      req.method !== 'GET' &&
      req.method !== 'HEAD' &&
      !headers['Content-Type'] &&
      !headers['content-type']
    ) {
      headers['Content-Type'] = 'application/json';
    }

    return headers;
  }

  private getAdminToken(): string {
    const token = this.authUserService.getToken();

    if (token || !environment.mobile) {
      return token;
    }

    return this.localStorageService.getItem('token');
  }

  private buildApiOriginPrefixes(): string[] {
    const { domainApiUrl, domainSSOUrl, domainNotificationsUrl, baseUrlWithSegment } = environment.EduSpireApi;
    return [domainApiUrl, baseUrlWithSegment, domainSSOUrl, domainNotificationsUrl].filter(
      (url): url is string => typeof url === 'string' && url.startsWith('http')
    );
  }

  private async executeNativeRequest(req: HttpRequest<unknown>): Promise<HttpEvent<unknown>> {
    const headers = this.buildNativeHeaders(req);
    const data = this.buildNativeRequestBody(req, headers);

    const options = {
      url: req.urlWithParams,
      headers,
      ...(data !== undefined ? { data } : {})
    };

    let response: CapacitorHttpResponse;
    try {
      response = await this.invokeCapacitorHttp(req.method, options);
    } catch (error) {
      // FLEX-4370: Capacitor iOS rejects with `{ code: 'NSURLErrorDomain', message }`.
      // Normalize so ErrorInterceptor does not treat the NSError domain as an API translate key.
      const nativeError = error as { message?: string; errorMessage?: string; code?: string };
      throw new HttpErrorResponse({
        error: {
          message: nativeError?.message || nativeError?.errorMessage || 'Native Http Error',
          nativeCode: nativeError?.code
        },
        status: 0,
        statusText: 'Native Http Error',
        url: req.urlWithParams
      });
    }

    if (response.status < 200 || response.status >= 400) {
      throw new HttpErrorResponse({
        error: response.data,
        headers: this.toHttpHeaders(response.headers),
        status: response.status,
        statusText: 'Http Error',
        url: req.urlWithParams
      });
    }

    return new HttpResponse({
      body: this.parseResponseBody(response.data, req.responseType),
      headers: this.toHttpHeaders(response.headers),
      status: response.status,
      url: req.urlWithParams
    });
  }

  private invokeCapacitorHttp(
    method: string,
    options: { url: string; headers: Record<string, string>; data?: unknown }
  ): Promise<CapacitorHttpResponse> {
    switch (method.toUpperCase()) {
      case 'GET':
        return CapacitorHttp.get(options);
      case 'POST':
        return CapacitorHttp.post(options);
      case 'PUT':
        return CapacitorHttp.put(options);
      case 'PATCH':
        return CapacitorHttp.patch(options);
      case 'DELETE':
        return CapacitorHttp.delete(options);
      default:
        return CapacitorHttp.request({ ...options, method: method.toUpperCase() });
    }
  }

  private toHttpHeaders(headers: CapacitorHttpResponse['headers']): HttpHeaders {
    if (!headers || typeof headers !== 'object') {
      return new HttpHeaders();
    }
    return new HttpHeaders(headers as Record<string, string>);
  }

  private parseResponseBody(data: unknown, responseType?: string): unknown {
    if (typeof data !== 'string') {
      return data;
    }

    const shouldParseJson =
      !responseType || responseType === 'json' || responseType === 'text';

    if (!shouldParseJson) {
      return data;
    }

    const trimmed = data.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return JSON.parse(data);
      } catch {
        return data;
      }
    }

    return data;
  }

  /** CapacitorHttp expects JSON bodies as a string with Content-Type set (WebView XHR auto-serializes). */
  private buildNativeRequestBody(req: HttpRequest<unknown>, headers: Record<string, string>): unknown | undefined {
    if (req.body == null || req.method === 'GET' || req.method === 'HEAD') {
      return undefined;
    }

    const hasContentType = Object.keys(headers).some(key => key.toLowerCase() === 'content-type');
    const isJsonObject =
      typeof req.body === 'object' && !(req.body instanceof ArrayBuffer) && !(req.body instanceof Blob);

    if (isJsonObject && !hasContentType) {
      headers['Content-Type'] = 'application/json';
    }

    if (isJsonObject && (headers['Content-Type']?.includes('application/json') || headers['content-type']?.includes('application/json'))) {
      return JSON.stringify(req.body);
    }

    return req.body;
  }
}
